// Cookie consent helpers. Reads/writes the `ct_cookie_consent` cookie
// (1 year, SameSite=Lax). Future analytics/marketing scripts should
// gate on `hasConsent('analytics' | 'marketing')`.

const COOKIE_NAME = "ct_cookie_consent";
const ONE_YEAR = 60 * 60 * 24 * 365;

export type ConsentChoices = {
  essential: true;
  analytics: boolean;
  marketing: boolean;
  ts: number;
};

export function readConsent(): ConsentChoices | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${COOKIE_NAME}=`));
  if (!match) return null;
  try {
    const json = decodeURIComponent(match.split("=")[1]);
    const parsed = JSON.parse(atob(json)) as ConsentChoices;
    if (typeof parsed !== "object" || parsed === null) return null;
    return { ...parsed, essential: true };
  } catch {
    return null;
  }
}

export function writeConsent(choices: Omit<ConsentChoices, "essential" | "ts">): ConsentChoices {
  const payload: ConsentChoices = {
    essential: true,
    analytics: choices.analytics,
    marketing: choices.marketing,
    ts: Date.now(),
  };
  if (typeof document !== "undefined") {
    const value = encodeURIComponent(btoa(JSON.stringify(payload)));
    document.cookie = `${COOKIE_NAME}=${value}; Max-Age=${ONE_YEAR}; Path=/; SameSite=Lax`;
  }
  return payload;
}

export function clearConsent() {
  if (typeof document === "undefined") return;
  document.cookie = `${COOKIE_NAME}=; Max-Age=0; Path=/; SameSite=Lax`;
}

export function hasConsent(kind: "analytics" | "marketing"): boolean {
  const c = readConsent();
  if (!c) return false;
  return kind === "analytics" ? c.analytics : c.marketing;
}

export function getOrCreateSessionId(): string {
  if (typeof window === "undefined") return "ssr";
  const KEY = "ct_session_id";
  let id = window.localStorage.getItem(KEY);
  if (!id) {
    id = crypto.randomUUID();
    window.localStorage.setItem(KEY, id);
  }
  return id;
}